import { useEffect, useRef } from "react";
import type { NavigateFunction } from "react-router-dom";

export const UNSAVED_CHANGES_CONFIRM_MESSAGE =
  "Are you sure you want to leave this page? Any unsaved changes will be lost.";

/**
 * Hand the decision to an in-app dialog instead of `window.confirm`.
 *
 * Return `true` to say "I have taken this over" — the hook then does NOTHING
 * else, and it is the caller's job to call `proceed()` if the user chooses to
 * leave (or simply to forget it if they stay). Return `false` and the hook falls
 * through to the native confirm exactly as before.
 *
 * The awkward shape is forced by the platform, not chosen: `window.confirm` is
 * synchronous and a React dialog is not, so the "navigate" half of this handler
 * has to survive as a continuation until an answer arrives. `proceed` closes
 * over the re-entrancy flag, so a deferred leave still sets `isNavigatingRef`
 * before `navigate(-1)` and the re-fired `popstate` is still swallowed.
 *
 * RE-ENTRANCY CONTRACT (PR #363 review, MEDIUM-1): while the caller's dialog is
 * already open, a further Back press re-enters this handler and calls the
 * decision function AGAIN. The caller must then return `true` WITHOUT storing
 * the new continuation — the question on screen stands, and the user's single
 * answer resolves it exactly once. Replacing the stored continuation
 * re-answers a question the user is still being asked. The host implements
 * this via `decideNoteLeaveRequest` (`noteLeavePrompt.ts`), where the rule is
 * tested.
 */
export type RequestLeaveDecision = (proceed: () => void) => boolean;

/**
 * AM-5694 (host side) — blocks the browser Back/Forward buttons while a
 * dirty (unsaved-changes) native write form is mounted, via the manual
 * `popstate` hack. Mirrors Provider Portal's
 * `CreateAuthV2/index.tsx` (~lines 313-366) pattern: push a dummy history
 * entry up front, and on `popstate` re-push + `window.confirm` before
 * actually letting the user leave.
 *
 * Why not React Router's `useBlocker`/`usePrompt`: both REQUIRE a data
 * router (`createBrowserRouter`). This app renders a plain `<BrowserRouter>`
 * (see App.tsx's `AppRoutes`) — calling `useBlocker` there throws
 * immediately, so it can't be used here.
 *
 * `isDirty` is a PREDICATE, not a ref, because more than one write surface can
 * be guarded at once — the caller ORs its forms together (AM-5921 added Create
 * Claim alongside Create Auth). It is called at event time, so it always sees
 * live state.
 *
 * Scope — this hook covers ONLY the browser Back/Forward buttons.
 *   - Tab-close/refresh is already covered by @astrana/ui's own
 *     `beforeunload` guards, self-attached inside CreateAuthForm (module-side,
 *     v0.4.39+, AM-5694) and CreateClaimForm (AM-5921) — do NOT add a second
 *     `beforeunload` listener here, it would just double-prompt.
 *   - In-app "leave the profile" actions (header/"Back to Home" button and
 *     the EHR patient-switch auto-navigate) are `navigate()` pushes, not
 *     browser Back — `popstate` never fires for those. They're guarded
 *     separately at their call sites via `confirmLeaveIfDirty` in
 *     MemberProfilePage.tsx. SwitchPatientModal's Navigate action is
 *     deliberately NOT guarded (AM-5706): the modal is itself the
 *     confirmation affordance, so a `window.confirm` on top would
 *     double-prompt.
 *
 * THE VISIT NOTE IS THE ONE SURFACE THAT DOES NOT USE THE NATIVE CONFIRM
 * (2026-08-19). PR #362 folded it into this guard, so leaving a dirty note
 * showed the form-shaped browser alert above — "Any unsaved changes will be
 * lost" — which is both the wrong shape and, since the note autosaves a draft,
 * not true. The note's own prompt is @astrana/ui's `LeaveNotePrompt`, opened via
 * the optional `requestLeave` argument below; see MemberProfilePage's
 * `requestNoteLeave`.
 *
 * WHAT STILL CANNOT BE A CUSTOM DIALOG, EVER: tab close and refresh. Browsers
 * deliberately ignore custom `beforeunload` text and show their own wording, so
 * that path stays the browser's prompt for every surface here, note included.
 * Nothing in this file can change that.
 */
export function useUnsavedChangesGuard(
  isDirty: () => boolean,
  navigate: NavigateFunction,
  requestLeave?: RequestLeaveDecision,
) {
  // Re-entrancy guard: our own `navigate(-1)` (below) is itself a browser
  // back navigation and will re-fire `popstate` — without this, the
  // re-fired event would see the same dirty state (not yet cleared, since the
  // page is mid-unmount) and re-prompt.
  const isNavigatingRef = useRef(false);
  const hasPushedInitialEntryRef = useRef(false);
  // Latest-ref pin: the listener is registered once (the effect depends on
  // `navigate` alone), so calling the predicate through a ref keeps a caller
  // that passes a fresh closure each render from being frozen to its
  // first-render identity.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;
  // Same latest-ref pin, same reason: the host's dialog opener is a fresh
  // closure every render (it captures `setState`), and the listener is
  // registered once.
  const requestLeaveRef = useRef(requestLeave);
  requestLeaveRef.current = requestLeave;

  useEffect(() => {
    const handlePopState = () => {
      if (isNavigatingRef.current) {
        // ONE-SHOT, not a permanent latch. This branch exists to swallow the
        // single popstate echo of our own `navigate(-1)` below — but a
        // confirmed leave does not always unmount this page (the dummy-entry
        // arithmetic can land the back navigation on the same URL, and under
        // the keep-alive tab shell the page survives regardless). Leaving the
        // flag raised therefore killed the guard for the REST OF THE SESSION:
        // every later Back left a dirty note with no prompt of any kind
        // (PR #363 review, MEDIUM-1). Swallow exactly the one echo, then
        // re-arm.
        isNavigatingRef.current = false;
        return;
      }
      if (!isDirtyRef.current()) return;

      // The browser has already moved back one entry by the time this fires.
      // Push the current URL back on top so the address bar/route doesn't
      // change out from under the user while the confirm dialog is up.
      window.history.pushState(null, "", window.location.href);

      // The actual leave, as a continuation. `isNavigatingRef` is set inside it
      // rather than at the call site so the flag is raised whether the answer
      // came from the synchronous confirm below or from a dialog several
      // seconds later.
      const proceed = () => {
        isNavigatingRef.current = true;
        navigate(-1);
      };

      // W-leave: an in-app dialog answers instead, when the caller says it can.
      // It returns false for the surfaces it does not own (a dirty Create Auth /
      // Create Claim form is genuine data loss and keeps the native confirm), so
      // this is a fall-through, not a replacement.
      if (requestLeaveRef.current?.(proceed)) return;

      if (window.confirm(UNSAVED_CHANGES_CONFIRM_MESSAGE)) proceed();
    };

    // Push exactly one dummy entry per mount (not per dirty-change) so the
    // history stack always has a "buffer" entry to absorb the first back
    // press, whether or not the form ever becomes dirty this session.
    if (!hasPushedInitialEntryRef.current) {
      window.history.pushState(null, "", window.location.href);
      hasPushedInitialEntryRef.current = true;
    }

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);
}
